import "./edit-quotation.scss";

import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderButton from "@shared/ui/header-button/header-button";
import HeaderContent from "@shared/ui/header-content/header-content";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useMemo } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { FaChevronLeft } from "react-icons/fa6";

import { currencyFormat } from "../../../shared/utility/formats/currency-format";
import PreviewFile from "../../preview-file/preview-file";
import FormQuotation from "../form-quotation/form-quotation";
import QuoteAccommodation from "../form-quotation/quote-accommodation";
import QuoteActivities from "../form-quotation/quote-activities";
import QuoteTransport from "../form-quotation/quote-transport";
import { useEditQuotation } from "./edit-quotation.hook";

export function EditQuotation() {
  const {
    quotationData,
    isLoading,
    quotationError,
    formMethod,
    actions,
    fileRef,
    downloading,
    handleBack,
    handleAction,
  } = useEditQuotation();

  const { control, setValue } = formMethod;

  const watchedFields = useWatch({
    control,
    name: ["activities", "transport", "accommodation", "agentMarkup"],
  });

  const totalAmount = useMemo(() => {
    const [activities, transport, accommodation, agentMarkup] =
      watchedFields;

    const sumArray = (arr: any, calculateChildRate = false) => {
      if (!Array.isArray(arr)) return 0;
      if (calculateChildRate) {
        return arr.reduce((sum, item) => sum + (item?.childRate || 0), 0);
      }
      return arr.reduce((sum, item) => sum + (item?.amount || 0), 0);
    };
    const sumAccommodation = (arr: any) => {
      if (!Array.isArray(arr)) return 0;
      return arr.reduce(
        (sum, item) => sum + item.price,
        0
      );
    };

    const sumChildTransport = (arr: any) => {
      if (!Array.isArray(arr)) return 0;
      return arr.reduce((sum, item) => sum + (item?.amount || 0), 0)
    }

    let childSum = 0;
    let adultSum = 0;
    let sum = 0;
    if (activities) {
      adultSum += sumArray(activities);
      childSum += sumArray(activities, true);
    }
    if (transport) {
      adultSum += sumArray(transport);
      let childTransports: any[] = []
      transport.forEach((item) => {
        if (item.childPrices) {
          childTransports = [...childTransports, ...(item.childPrices)]
        }
      })
      childSum += sumChildTransport(childTransports)
    }
    if (accommodation) {
      adultSum += sumAccommodation(accommodation);
    }
    if (agentMarkup) {
      sum = (childSum + adultSum) * (1 + agentMarkup / 100);
    }
    else {
      sum = (childSum + adultSum);
    }
    setValue("childRateTotal", childSum);
    setValue("adultRateTotal", adultSum);
    setValue("amount", sum);
    return sum;
  }, [watchedFields]);

  const title = <h1>Edit Quotation</h1>;
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection
        title="No Data"
        message="No data found in selected record. Please try again."
      />
    </div>
  );
  const mainContent = (
    <div>
      <div className="flex justify-end">
        <span className="text-2xl font-bold">
          {totalAmount
            ? `Total: ${currencyFormat(totalAmount)}`
            : `Total: ${currencyFormat(0)}`}
        </span>
      </div>
      <section className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        <div>
          <FormQuotation isEditing />
          <QuoteTransport />
          <QuoteAccommodation />
          <QuoteActivities />
        </div>
        <div>
          <div className="shadow-lg">
            <PreviewFile fileRef={fileRef} downloading={downloading} />
          </div>
        </div>
      </section>
    </div>
  );

  return (
    <div id="EditQuotation" className="quotation">
      <HeaderContent
        title={title}
        backIcon={<FaChevronLeft />}
        onBack={() => handleBack()}
      >
        <HeaderButton actions={actions} onAction={handleAction} />
      </HeaderContent>

      <section className="p-7">
        <ConfirmDialog />
        <FormProvider {...formMethod}>
          {isLoading && displayLoading}
          {quotationError && !isLoading && displayError}
          {!isLoading ? mainContent : null}
        </FormProvider>
      </section>
    </div>
  );
}

export default EditQuotation;
